import { useMemo } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Input } from '@/components/ui';
import type { FacetGroup, FacetOption } from './facetTypes';

function FacetRow({
    group,
    option,
    onSelect,
}: {
    group: FacetGroup;
    option: FacetOption;
    onSelect: () => void;
}) {
    const { t } = useTranslation();
    const disabled = Boolean(option.disabledReason);
    return (
        // EAA-1804: root debugging handle, matching this file's own acc-rail__* BEM
        // element naming.
        <li key={option.key} className="acc-rail__item">
            <Checkbox
                className={
                    'acc-rail__option' +
                    (option.selected ? ' acc-rail__option--active' : '') +
                    (disabled ? ' acc-rail__option--disabled' : '') +
                    (option.tone === 'warn' ? ' acc-rail__option--warn' : '')
                }
                checked={option.selected}
                disabled={disabled}
                onChange={onSelect}
                title={option.disabledReason}
                aria-label={t('console.accounts.facetRail.optionAriaLabel', {
                    group: group.label,
                    option: option.label,
                })}
            >
                <span className="acc-rail__label">{option.label}</span>
                <span className="acc-rail__count">
                    {option.count === null ? '—' : option.count.toLocaleString('vi-VN')}
                </span>
            </Checkbox>
        </li>
    );
}

export interface AccountsFacetRailProps {
    groups: FacetGroup[];
    onSelect: (groupKey: string, optionKey: string) => void;
    hasActiveFilters: boolean;
    onClear: () => void;
    /**
     * EAA-1839 (frame 35): "Lọc nhanh trong danh mục…" — narrows the RAIL, not the
     * table. Purely client-side and deliberately so: it hides options whose label
     * does not match, which is a navigation aid for a rail that now carries six
     * groups. A group whose every option is hidden drops out with it, so the rail
     * never shows an empty titled group.
     */
    quickFilter?: { value: string; onChange: (value: string) => void };
    /** Closing line under all groups (frame 35: "Bộ lọc cộng dồn. Bỏ hết để về danh sách đầy đủ."). */
    note?: string;
    /**
     * EAA-1839 (2026-08-21, user request): the rail now renders in two places —
     * the sidebar grid column (`'sidebar'`, the default and the ONLY layout
     * frame 35 draws) and, when the operator switches "Vị trí bộ lọc" to
     * "Trên bảng" in the Hiển thị popover, inside a `FilterPopover` panel
     * opened from a collapsed trigger above the table (`'popover'`). This is
     * the SAME component and the SAME `FacetGroup[]` rendering in both cases —
     * only the outer sizing differs (`.acc-rail--popover` caps height to the
     * viewport instead of stretching to the grid row) — because a second
     * renderer for one data shape is exactly the drift this codebase's own
     * docstrings (see this file's header) complain about.
     */
    variant?: 'sidebar' | 'popover';
}

/**
 * EAA-1820: the ONE facet rail both `/console/accounts` (realm) and
 * `/console/hierarchy/accounts` (org) render — see `shared/facetTypes.ts` for why
 * a single `FacetGroup[]` shape could finally carry both. This file replaces the
 * realm's `accounts/AccountsFacetRail.tsx` (bespoke `facetCounts` object,
 * multi-select only) and the org's `hierarchy/scopedAccounts/ScopedAccountsFacets.tsx`
 * (single-select only, nullable counts, `disabledReason`/`tone`) — the union of
 * what both needed was already almost entirely the org shape, so this is that
 * shape rendered generically.
 *
 * The rail does not know or care whether a group is multi- or single-select:
 * every click calls `onSelect(groupKey, optionKey)` and the adapter (realm:
 * toggle-in-array; org: replace-the-one-value, per its exact-match query params)
 * decides what selecting it means. See each adapter's `selectFacet`.
 */
export function AccountsFacetRail({
    groups,
    onSelect,
    hasActiveFilters,
    onClear,
    quickFilter,
    note,
    variant = 'sidebar',
}: AccountsFacetRailProps) {
    const { t } = useTranslation();

    const needle = quickFilter?.value.trim().toLowerCase() ?? '';
    const visibleGroups = useMemo(() => {
        if (!needle) return groups;
        return groups
            .map((group) => ({
                ...group,
                options: group.options.filter(
                    (option) =>
                        option.label.toLowerCase().includes(needle) ||
                        group.label.toLowerCase().includes(needle)
                ),
            }))
            .filter((group) => group.options.length > 0);
    }, [groups, needle]);

    return (
        <aside
            className={'acc-rail' + (variant === 'popover' ? ' acc-rail--popover' : '')}
            aria-label={t('console.accounts.facetRail.ariaLabel')}
        >
            {quickFilter && (
                <Input
                    className="acc-rail__quick"
                    value={quickFilter.value}
                    onChange={(event) => quickFilter.onChange(event.target.value)}
                    placeholder={t('console.accounts.facetRail.quickFilterPlaceholder')}
                    aria-label={t('console.accounts.facetRail.quickFilterAriaLabel')}
                    prefix={<SearchOutlined />}
                    allowClear
                />
            )}

            {visibleGroups.map((group) => (
                <div key={group.key} className="acc-rail__group">
                    <h3 className="acc-rail__title">
                        {group.label}
                        {group.hint}
                    </h3>
                    <ul className="acc-rail__list">
                        {group.options.map((option) => (
                            <FacetRow
                                key={option.key}
                                group={group}
                                option={option}
                                onSelect={() => onSelect(group.key, option.key)}
                            />
                        ))}
                    </ul>
                    {group.note && <p className="acc-rail__note">{group.note}</p>}
                </div>
            ))}

            {needle && visibleGroups.length === 0 && (
                <p className="acc-rail__note">
                    {t('console.accounts.facetRail.quickFilterNoMatch')}
                </p>
            )}

            {note && <p className="acc-rail__note acc-rail__note--footer">{note}</p>}

            {hasActiveFilters && (
                <Button className="acc-rail__clear" onClick={onClear}>
                    {t('console.accounts.facetRail.clearFiltersButton')}
                </Button>
            )}
        </aside>
    );
}

export default AccountsFacetRail;
